import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function Register() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  const inviterParam = searchParams.get('inviter') || '';

  const handleGoogleResponse = useCallback(async (response) => {
    setGoogleLoading(true);
    setError('');
    try {
      const result = await loginWithGoogle(response.credential, refCode);
      const inviterId = result?.inviterId || inviterParam;
      navigate(inviterId ? `/user/${inviterId}` : (searchParams.get('redirect') || '/'));
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setGoogleLoading(false);
    }
  }, [loginWithGoogle, navigate, refCode, inviterParam, searchParams]);

  useEffect(() => {
    // Initialize Google Sign-In
    if (!GOOGLE_CLIENT_ID) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      window.google?.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      window.google?.accounts.id.renderButton(
        document.getElementById('google-signup-btn'),
        { 
          theme: 'filled_black', 
          size: 'large',
          width: '100%',
          text: 'signup_with'
        }
      );
    };

    return () => {
      document.body.removeChild(script);
    };
  }, [handleGoogleResponse]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // register() now sets the token + user internally (server returns
      // a JWT in the register response) — no more follow-up login call.
      const result = await register(email, username, password, displayName, refCode);
      const inviterId = result?.data?.inviterId || inviterParam;
      navigate(inviterId ? `/user/${inviterId}` : (searchParams.get('redirect') || '/'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Travel Together</h1>
        <h2>Create Account</h2>
        
        {error && <div className="error">{error}</div>}

        {GOOGLE_CLIENT_ID && (
          <>
            <div id="google-signup-btn" className="google-btn-container"></div>
            {googleLoading && <p className="loading-text">Signing up with Google...</p>}
            <div className="divider">
              <span>or</span>
            </div>
          </>
        )}
        
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password (8+ characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
          />
          <button type="submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Register'}
          </button>
        </form>
        
        <p>
          Already have an account? <Link to="/login">Login</Link>
        </p>
      </div>
    </div>
  );
}

